'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';

interface SignUpFormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

interface SignUpFormProps {
  onSwitchToLogin: () => void;
}

export default function SignUpForm({ onSwitchToLogin }: SignUpFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFormData>();

  const password = watch('password');

  const onSubmit = async (_data: SignUpFormData) => {
    setLoading(true);
    // TODO: Backend integration — POST /api/auth/register with { fullName, email, password }
    await new Promise((r) => setTimeout(r, 1500));
    setLoading(false);
    toast.success('Konto erstellt! Willkommen bei SmartEmailHub.');
    window.location.href = '/';
  };

  return (
    <div className="space-y-5">
      <div className="text-center mb-2">
        <h1 className="text-2xl font-bold text-foreground mb-1">Konto erstellen</h1>
        <p className="text-sm text-muted-foreground">Starte kostenlos — keine Kreditkarte nötig</p>
      </div>

      {/* OAuth */}
      <div className="space-y-2.5">
        <button className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-xl border border-border bg-card hover:bg-muted transition-all duration-150 text-sm font-semibold text-foreground active:scale-95">
          <div className="w-5 h-5 rounded bg-red-500 flex items-center justify-center text-white text-xs font-bold">G</div>
          Mit Google registrieren
        </button>
        <button className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-xl border border-border bg-card hover:bg-muted transition-all duration-150 text-sm font-semibold text-foreground active:scale-95">
          <div className="w-5 h-5 rounded bg-blue-600 flex items-center justify-center text-white text-xs font-bold">M</div>
          Mit Microsoft registrieren
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-medium">oder per E-Mail</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div>
          <label htmlFor="signup-name" className="label-text">Vollständiger Name</label>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            placeholder="Max Mustermann"
            className={`input-field ${errors.fullName ? 'border-red-400' : ''}`}
            {...register('fullName', {
              required: 'Name ist erforderlich.',
              minLength: { value: 2, message: 'Mindestens 2 Zeichen.' },
            })}
          />
          {errors.fullName && <p className="error-text"><Icon name="ExclamationCircleIcon" size={12} variant="outline" />{errors.fullName.message}</p>}
        </div>

        <div>
          <label htmlFor="signup-email" className="label-text">E-Mail-Adresse</label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="max@beispiel.de"
            className={`input-field ${errors.email ? 'border-red-400' : ''}`}
            {...register('email', {
              required: 'E-Mail ist erforderlich.',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Ungültige E-Mail-Adresse.' },
            })}
          />
          {errors.email && <p className="error-text"><Icon name="ExclamationCircleIcon" size={12} variant="outline" />{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="signup-password" className="label-text">Passwort</label>
          <p className="helper-text mb-1.5">Mindestens 8 Zeichen, ein Großbuchstabe und eine Zahl.</p>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••"
              className={`input-field pr-10 ${errors.password ? 'border-red-400' : ''}`}
              {...register('password', {
                required: 'Passwort ist erforderlich.',
                minLength: { value: 8, message: 'Mindestens 8 Zeichen.' },
                pattern: {
                  value: /^(?=.*[A-Z])(?=.*\d).+$/,
                  message: 'Mindestens ein Großbuchstabe und eine Zahl erforderlich.',
                },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((p) => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Passwort umschalten"
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} variant="outline" />
            </button>
          </div>
          {errors.password && <p className="error-text"><Icon name="ExclamationCircleIcon" size={12} variant="outline" />{errors.password.message}</p>}
        </div>

        <div>
          <label htmlFor="signup-confirm" className="label-text">Passwort bestätigen</label>
          <div className="relative">
            <input
              id="signup-confirm"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••"
              className={`input-field pr-10 ${errors.confirmPassword ? 'border-red-400' : ''}`}
              {...register('confirmPassword', {
                required: 'Bitte Passwort bestätigen.',
                validate: (val) => val === password || 'Passwörter stimmen nicht überein.',
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirm((p) => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Passwort bestätigung umschalten"
            >
              <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={16} variant="outline" />
            </button>
          </div>
          {errors.confirmPassword && <p className="error-text"><Icon name="ExclamationCircleIcon" size={12} variant="outline" />{errors.confirmPassword.message}</p>}
        </div>

        <div className="flex items-start gap-2">
          <input
            id="acceptTerms"
            type="checkbox"
            className="w-4 h-4 mt-0.5 rounded border-border text-primary focus:ring-ring"
            {...register('acceptTerms', { required: 'Du musst die Nutzungsbedingungen akzeptieren.' })}
          />
          <label htmlFor="acceptTerms" className="text-sm text-foreground cursor-pointer leading-snug">
            Ich akzeptiere die{' '}
            <a href="#" className="text-primary hover:underline">Nutzungsbedingungen</a>
            {' '}und die{' '}
            <a href="#" className="text-primary hover:underline">Datenschutzerklärung</a>.
          </label>
        </div>
        {errors.acceptTerms && <p className="error-text -mt-2"><Icon name="ExclamationCircleIcon" size={12} variant="outline" />{errors.acceptTerms.message}</p>}

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full justify-center py-3 text-sm"
        >
          {loading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" />
              Konto wird erstellt…
            </>
          ) : (
            'Kostenlos starten'
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground">
        Bereits registriert?{' '}
        <button onClick={onSwitchToLogin} className="text-primary font-semibold hover:underline">
          Jetzt anmelden
        </button>
      </p>
    </div>
  );
}